import { IngredientItem, IngredientItemData } from './ingredient-item';
import { IngredientItemFactory } from './factory';
import { ReadCollectionGql } from './gql';

type Data = {
  documents: IngredientItemData[];
  nextToken: string;
};

class IngredientItemCollection {

  readonly businessId: string;
  readonly documents: IngredientItem[];
  readonly nextToken: string;

  constructor(businessId: string = '', data: Data = { documents: [], nextToken: '' }) {
    this.businessId = businessId;
    this.documents = IngredientItem.sort(data.documents.map(it => IngredientItemFactory.create(it)));
    this.nextToken = data.nextToken;
  }

  async readAll(): Promise<this> {
    let collection = this;
    while (collection.nextToken) {
      const { businessId, nextToken } = collection;
      if (!businessId) continue;
      const gql = await new ReadCollectionGql().fetch({ businessId, nextToken });
      if (!gql.result) throw new Error('invalid result');
      collection = collection.merge(new IngredientItemCollection(businessId, gql.result));
    }
    return collection;
  }

  merge(collection: IngredientItemCollection): this {
    const documents = IngredientItem.sort([...this.documents, ...collection.documents]);
    const nextToken = collection.nextToken;
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents, nextToken });
  }

  add(item: IngredientItem): this {
    const documents = IngredientItem.sort([...this.documents, item]);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  addBatch(items: IngredientItem[]): this {
    const documents = IngredientItem.sort([...this.documents, ...items]);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  replace(item: IngredientItem): this {
    const documents = IngredientItem.sort(this.documents.map(it => it.id === item.id ? item : it));
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  remove(item: IngredientItem): this {
    const documents = this.documents.filter(it => it.id !== item.id);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  toJSON(): Data {
    const { nextToken } = this;
    const documents = this.documents.map(it => it.toJSON());
    return { documents, nextToken };
  }

  getUnitSymbols(): string[] {
    return [...new Set(this.documents.map(it => it.unitSymbol ?? '').filter(it => !!it))].sort();
  }

  static async read(businessId: string): Promise<IngredientItemCollection> {
    const gql = await new ReadCollectionGql().fetch({ businessId });
    if (!gql.result) throw new Error('invalid result');
    return new IngredientItemCollection(businessId, gql.result);
  }

}

export { IngredientItemCollection };
export type { Data as IngredientItemCollectionData };
export type { ReadCollectionGqlDataToFetch as ReadIngredientItemCollectionDataToFetch } from './gql';
